import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { useRegions } from "../contexts/RegionContext";
import { useVerticals } from "../contexts/VerticalsContext";
import Modal from '../components/Modal';
import Button from '../components/Buttons';
import { getAllCountryOptions, getStateOptions } from "../utils/countries";
import { shouldShowRegionPicker, defaultRegionId, regionCountryCodes } from "../utils/regions";
import { useGoogleMapsLoader } from "../hooks/useGoogleMapsLoader";
import { useAddressAutocomplete } from "../hooks/useAddressAutocomplete";

const CreateBusinessUnitModal = ({ isOpen, onClose, onBusinessUnitCreated }) => {
  const apiCall = useApi();
  const { user, refreshUserRoles } = useAuth();
  const regions = useRegions();
  const { verticals } = useVerticals();
  const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");

  const [isBUSubmitting, setIsBUSubmitting] = useState(false);
  const [isSelf, setIsSelf] = useState(false);
  const [loadingSelf, setLoadingSelf] = useState(false);
  const [formData, setFormData] = useState({
    BusinessUnitName: "",
    RegionId: String(selectedOrg?.regionId || ""),
    OrganizationId: "",
    VerticalId: "",
    SubVerticalId: "",
    Domain: "",
    BusinessUnitOwner: "",
    BusinessUnitCountry: "",
    BusinessUnitAddress: "",
    BusinessUnitCity: "",
    BusinessUnitState: "",
    BusinessUnitPostalCode: "",
    BusinessUnitPhoneNo: "",
    BusinessUnitStatus: "Active",
  });

  // Reset form each time the modal opens (component stays mounted, only isOpen toggles)
  useEffect(() => {
    if (isOpen) {
      setFormData({
        BusinessUnitName: "",
        RegionId: String(selectedOrg?.regionId || ""),
        OrganizationId: "",
        VerticalId: "",
        SubVerticalId: "",
        Domain: "",
        BusinessUnitOwner: "",
        BusinessUnitCountry: "",
        BusinessUnitAddress: "",
        BusinessUnitCity: "",
        BusinessUnitState: "",
        BusinessUnitPostalCode: "",
        BusinessUnitPhoneNo: "",
        BusinessUnitStatus: "Active",
      });
      setIsSelf(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Google Maps
  const { isLoaded: googleIsLoaded, loadError: googleLoadError, isInactive: googleMapsInactive } = useGoogleMapsLoader();

  // Keep RegionId on a configured region: the org's region when configured, the only region
  // when there is one (the picker is then hidden), otherwise the user's choice.
  useEffect(() => {
    // Wait for the regions to load: an empty list would clear the org's prefilled region.
    if (!regions?.length) return;
    const id = defaultRegionId(regions, formData.RegionId);
    if (id !== formData.RegionId) {
      setFormData(prev => ({ ...prev, RegionId: id }));
    }
  }, [regions, formData.RegionId]);

  const showRegionPicker = shouldShowRegionPicker(regions);
  const selectedRegion = regions.find(r => String(r.regionId) === String(formData.RegionId));

  // Keyed on countryFilterKey (not the display name, which an operator may override).
  const regionCodes = useMemo(
    () => regionCountryCodes(selectedRegion),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRegion?.countryFilterKey]
  );

  const countryOptions = getAllCountryOptions();

  // Field adapter: hook uses organization* keys; form uses BusinessUnit* keys
  const ORG_TO_BU = {
    organizationAddress1: 'BusinessUnitAddress',
    organizationCity:    'BusinessUnitCity',
    organizationState:   'BusinessUnitState',
    organizationZip:     'BusinessUnitPostalCode',
    organizationCountry: 'BusinessUnitCountry',
  };

  const handleBatchChange = useCallback((updates) => {
    setFormData(prev => {
      const mapped = {};
      for (const [key, val] of Object.entries(updates)) {
        mapped[ORG_TO_BU[key] ?? key] = val;
      }
      return { ...prev, ...mapped };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelfToggle = async (checked) => {
    setIsSelf(checked);
    if (!checked) {
      setFormData({
        BusinessUnitName: "",
        RegionId: String(selectedOrg?.regionId || ""),
        OrganizationId: "",
        VerticalId: "",
        SubVerticalId: "",
        Domain: "",
        BusinessUnitOwner: "",
        BusinessUnitCountry: "",
        BusinessUnitAddress: "",
        BusinessUnitCity: "",
        BusinessUnitState: "",
        BusinessUnitPostalCode: "",
        BusinessUnitPhoneNo: "",
        BusinessUnitStatus: "Active",
      });
      return;
    }
    setLoadingSelf(true);
    try {
      const orgs = await apiCall('GET', '/organizations');
      const currentOrgId = selectedOrg?.organizationId;
      const org = (orgs || []).find(o => String(o.organizationId) === String(currentOrgId));
      if (org) {
        setFormData(prev => ({
          ...prev,
          BusinessUnitName: org.organizationName || "",
          RegionId: String(org.regionId || prev.RegionId),
          VerticalId: String(org.verticalId || ""),
          SubVerticalId: String(org.subVerticalId || ""),
          Domain: org.domain || "",
          BusinessUnitCountry: org.organizationCountry || "",
          BusinessUnitAddress: org.organizationAddress1 || "",
          BusinessUnitCity: org.organizationCity || "",
          BusinessUnitState: org.organizationState || "",
          BusinessUnitPostalCode: org.organizationPostalCode || "",
          BusinessUnitPhoneNo: org.organizationContactNo1 || "",
        }));
      }
    } catch (err) {
      setIsSelf(false);
    } finally {
      setLoadingSelf(false);
    }
  };

  const { suggestions, showSuggestions, setShowSuggestions, handleSuggestionSelect, zipLoading } =
    useAddressAutocomplete({
      isLoaded:      googleIsLoaded,
      addressValue:  formData.BusinessUnitAddress,
      country:       formData.BusinessUnitCountry,
      regionCodes,
      zip:           formData.BusinessUnitPostalCode,
      city:          formData.BusinessUnitCity,
      state:         formData.BusinessUnitState,
      onBatchChange: handleBatchChange,
    });

  const addressWrapperRef = useRef(null);
  useEffect(() => {
    const close = (e) => {
      if (addressWrapperRef.current && !addressWrapperRef.current.contains(e.target))
        setShowSuggestions(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [setShowSuggestions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const org = JSON.parse(localStorage.getItem("selectedOrganization"));
    if (!org?.organizationId) {
      alert("No organization selected. Please select an organization first.");
      return;
    }

    setIsBUSubmitting(true);
    try {
      const submitData = {
        ...formData,
        OrganizationId: org.organizationId,
        TenantId: org.tenantId,
      };

      await apiCall("POST", "/businessunits", submitData);
      await refreshUserRoles();
      onBusinessUnitCreated();
      onClose();
    } catch (error) {} finally {
      setIsBUSubmitting(false);
    }
  };

  const inputClassName = "w-full border border-border rounded-md px-3 py-2 bg-input-bg text-content-primary focus:outline-none focus:ring-2 focus:ring-accent-cyan/50 focus:border-accent-cyan";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Business Unit" contentLabel="New Business Unit">
      <form onSubmit={handleSubmit} className="space-y-3">

        {/* Self checkbox */}
        <div className="flex items-center gap-2 pb-1 border-b border-border">
          <input
            type="checkbox"
            id="buSelf"
            checked={isSelf}
            onChange={e => handleSelfToggle(e.target.checked)}
            disabled={loadingSelf}
            className="w-4 h-4 accent-accent-cyan cursor-pointer"
          />
          <label htmlFor="buSelf" className="text-sm font-medium text-content-primary cursor-pointer select-none">
            Self
            {loadingSelf && <span className="ml-2 text-xs text-content-muted animate-pulse">Loading…</span>}
          </label>
          {isSelf && (
            <span className="ml-auto text-xs text-content-muted">
              Auto-filled from organization data
            </span>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-content-secondary mb-1">Business Unit Name <span className="text-red-500">*</span></label>
          <input type="text" value={formData.BusinessUnitName} onChange={e => setFormData({ ...formData, BusinessUnitName: e.target.value })} className={inputClassName} required disabled={isSelf} />
        </div>

        {/* Data Residency Region (hidden when the installation has one region) */}
        {showRegionPicker && (
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-1">Data Residency Region <span className="text-red-500">*</span></label>
          <select
            value={formData.RegionId}
            onChange={e => setFormData({ ...formData, RegionId: e.target.value })}
            className={inputClassName}
            required
            disabled={isSelf}
          >
            <option value="">-- Select Region --</option>
            {regions.map(r => (
              <option key={r.regionId} value={r.regionId}>{r.regionName}</option>
            ))}
          </select>
        </div>
        )}

        {/* Vertical */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-1">Vertical <span className="text-red-500">*</span></label>
          <select
            value={formData.VerticalId}
            onChange={e => setFormData({ ...formData, VerticalId: e.target.value, SubVerticalId: "" })}
            className={inputClassName}
            required
            disabled={isSelf}
          >
            <option value="">-- Select Vertical --</option>
            {verticals.map(v => (
              <option key={v.verticalId} value={v.verticalId}>{v.verticalName}</option>
            ))}
          </select>
        </div>

        {/* SubVertical */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-1">Sub-Vertical <span className="text-red-500">*</span></label>
          <select
            value={formData.SubVerticalId}
            onChange={e => setFormData({ ...formData, SubVerticalId: e.target.value })}
            className={`${inputClassName} disabled:opacity-50`}
            required
            disabled={isSelf || !formData.VerticalId}
          >
            <option value="">-- Select Sub-Vertical --</option>
            {verticals
              .find(v => v.verticalId === parseInt(formData.VerticalId))?.subVerticals
              ?.map(sv => (
                <option key={sv.subVerticalId} value={sv.subVerticalId}>{sv.subVerticalName}</option>
              ))}
          </select>
        </div>

        {/* Domain */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-1">Domain</label>
          <input type="text" value={formData.Domain} onChange={e => setFormData({ ...formData, Domain: e.target.value })} className={inputClassName} placeholder="e.g., example.com" disabled={isSelf} />
        </div>

        {/* Business Unit Owner */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-1">Business Unit Owner</label>
          <input type="text" value={formData.BusinessUnitOwner} onChange={e => setFormData({ ...formData, BusinessUnitOwner: e.target.value })} className={inputClassName} disabled={isSelf} />
        </div>

        {/* Country */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-1">Country</label>
          <select
            value={formData.BusinessUnitCountry}
            onChange={e => setFormData({ ...formData, BusinessUnitCountry: e.target.value, BusinessUnitState: '' })}
            className={inputClassName}
            disabled={isSelf}
          >
            {countryOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Address with Google Places autocomplete */}
        <div ref={addressWrapperRef} className="relative">
          <label className="block text-sm font-medium text-content-secondary mb-1">Address</label>
          <input
            type="text"
            value={formData.BusinessUnitAddress}
            onChange={e => setFormData({ ...formData, BusinessUnitAddress: e.target.value })}
            className={inputClassName}
            autoComplete="off"
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            placeholder={googleLoadError || googleMapsInactive ? 'Enter street address' : 'Start typing to search address…'}
            disabled={isSelf}
          />

          {showSuggestions && suggestions.length > 0 && (
            <ul
              className="absolute z-50 w-full bg-white border border-gray-200 rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.10)] overflow-hidden"
              style={{ top: 'calc(100% - 1rem)', marginTop: '2px' }}
            >
              {suggestions.map((s, i) => (
                <li key={s.placePrediction?.placeId || i} className="border-b border-gray-100 last:border-b-0">
                  <button
                    type="button"
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => handleSuggestionSelect(s)}
                    className="w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {s.placePrediction?.mainText?.text}
                    </p>
                    {s.placePrediction?.secondaryText?.text && (
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {s.placePrediction.secondaryText.text}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {googleLoadError && (
            <p className="text-xs text-gray-400 mt-1">Address autocomplete unavailable — please enter manually.</p>
          )}
        </div>

        {/* Postal Code | State / Province | City */}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">Postal Code</label>
            <input
              type="text"
              value={formData.BusinessUnitPostalCode}
              onChange={e => setFormData({ ...formData, BusinessUnitPostalCode: e.target.value })}
              className={inputClassName}
              disabled={isSelf}
            />
            {zipLoading && <p className="text-xs text-gray-400 mt-1 animate-pulse">Looking up…</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">State / Province</label>
            {formData.BusinessUnitCountry === 'US' ? (
              <select
                value={formData.BusinessUnitState}
                onChange={e => setFormData({ ...formData, BusinessUnitState: e.target.value })}
                className={inputClassName}
                disabled={isSelf}
              >
                {getStateOptions().map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={formData.BusinessUnitState}
                onChange={e => setFormData({ ...formData, BusinessUnitState: e.target.value })}
                className={inputClassName}
                placeholder="State / Province"
                disabled={isSelf}
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">City</label>
            <input
              type="text"
              value={formData.BusinessUnitCity}
              onChange={e => setFormData({ ...formData, BusinessUnitCity: e.target.value })}
              className={inputClassName}
              disabled={isSelf}
            />
          </div>
        </div>

        {/* Phone No */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-1">Phone No</label>
          <input type="text" value={formData.BusinessUnitPhoneNo} onChange={e => setFormData({ ...formData, BusinessUnitPhoneNo: e.target.value })} className={inputClassName} disabled={isSelf} />
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-1">Status</label>
          <select
            value={formData.BusinessUnitStatus}
            onChange={e => setFormData({ ...formData, BusinessUnitStatus: e.target.value })}
            className={inputClassName}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        {/* Created By — read-only */}
        <div>
          <label className="block text-sm font-medium text-content-secondary mb-1">Created By</label>
          <div className={`${inputClassName} text-content-muted cursor-default select-none`}>
            {user?.userName || user?.email || '—'}
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-4">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={isBUSubmitting}>
            {isBUSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              "Create Business Unit"
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateBusinessUnitModal;
